import { Group, Select } from '@byecode/ui'
import type { FlowNode, NodeTypes, OpenWeChatCustomerServiceActionConfig, VariableTree2 } from '@lighthouse/shared'
import {
    getAllCanBeUpstreamNodes,
    getFormDatasourceOptions,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    getViewVariableOption,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    nodeTypeOptions,
    PAGE_LINK_OPTION,
    useAtomData
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import { UnitNoAuth } from '../../Common/UnitNoAuth'
import * as SC from '../../styles'

interface OpenWeChatCustomerServiceActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes?: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    value: OpenWeChatCustomerServiceActionConfig
    onChange: (value: OpenWeChatCustomerServiceActionConfig) => void
}

export const OpenWeChatCustomerServiceActionConfigure: React.FC<OpenWeChatCustomerServiceActionConfigureProps> = ({
    allParentNodes,
    actionTypeSwitcher,
    nodeType,
    value,
    onChange
}) => {
    const disabledWithVersion = useIsDisabledWithVersion()
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const { viewDsId = '', formDsId = '', viewType } = useActionAdderDepParams()
    const formDataSource = useDataSource(appId, envId, formDsId)
    const viewDataSource = useDataSource(appId, envId, viewDsId)

    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes || []),
                      allUpstreamNodes: allParentNodes,
                      dataSourceList
                  })
                : undefined,
        [allParentNodes, dataSourceList, isInFlow, workflowList]
    )

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const formOption = useMemo(() => {
        if (formDataSource && !isInFlow) {
            return getFormDatasourceOptions({ dataSource: formDataSource })
        }
    }, [formDataSource, isInFlow])

    const { customViewOption } = useVariableCustomViewOption()

    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({
                dataSource: viewDataSource,
                viewType
            })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }
        return options
    }, [isInFlow, customViewOption, viewDataSource, viewType])

    const userOption = useGetUserRecordOptions()

    const wechatCustomerServiceAccount = useMemo(
        () => otherPlatformList?.find(item => item.type === 'WECHAT_MINI_PROGRAM'),
        [otherPlatformList]
    )

    if (!wechatCustomerServiceAccount) {
        return <UnitNoAuth type="WECHAT_MINI_PROGRAM" />
    }

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>企业微信 corpId</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            autoHeight
                            value={value.corpId}
                            onBlur={corpId => onChange({ ...value, corpId })}
                            placeholder="请输入企业微信 corpId"
                            viewOptions={viewOptions}
                            formOption={formOption}
                            pageOptions={isNotAutomation ? pageOptions : undefined}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            dataSourceList={dataSourceList}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>客服链接 URL</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            autoHeight
                            value={value.url}
                            onBlur={url => onChange({ ...value, url })}
                            placeholder="请输入客服链接 URL"
                            viewOptions={viewOptions}
                            formOption={formOption}
                            pageOptions={isNotAutomation ? [PAGE_LINK_OPTION, ...(pageOptions || [])] : undefined}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            dataSourceList={dataSourceList}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
        </SC.Container>
    )
}
